import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";
import { Loader2 } from "lucide-react";
import { useExchangeSecretKey } from "@/hooks/auth/useExchangeSecretKey";

/**
 * Decode JWT token and extract expiration time
 * @param token - JWT token string
 * @returns Decoded payload or null if invalid
 */
function decodeJWT(token: string): { exp?: number } | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

export const SalesforceCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setCookie] = useCookies(["auth_token", "refresh_token"]);
  const exchangeSecret = useExchangeSecretKey();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const secretKey = searchParams.get("secret_key");

    // If no secret_key in URL, redirect to login
    if (!secretKey) {
      setTimeout(() => navigate("/auth/login", { replace: true }), 2000);
      return;
    }

    // Prevent duplicate calls - only run if not already pending/success
    if (exchangeSecret.isPending || exchangeSecret.isSuccess) {
        return;
      }

      // Remove secret_key from URL immediately to prevent reuse on refresh
      window.history.replaceState({}, document.title, window.location.pathname);

    // Exchange secret key for JWT
      exchangeSecret.mutate(secretKey, {
        onSuccess: (data) => {
        const jwtToken = data.generateJwt.jwtToken;
        const refreshToken = data.generateJwt.refreshToken;
        console.log("🔑 JWT generated:", jwtToken);

        // Decode JWT to get expiration time
        const decoded = decodeJWT(jwtToken);
        const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
        const maxAge = decoded?.exp 
          ? decoded.exp - currentTime // Calculate remaining time
          : 7 * 24 * 60 * 60; // Fallback to 7 days if decode fails
        
        // Save JWT token to cookie with dynamic expiration
        setCookie("auth_token", jwtToken, {
          path: "/",
          secure: true, // Only send over HTTPS
          sameSite: "strict", // CSRF protection
          maxAge, // Use token's actual expiration time
        });

        // Save refresh token to cookie (30 days per Auth API doc; rotates on use)
        const refreshTokenMaxAge = 30 * 24 * 60 * 60; // 30 days in seconds
        setCookie("refresh_token", refreshToken, {
          path: "/",
          secure: true,
          sameSite: "strict",
          maxAge: refreshTokenMaxAge,
        });
        
        // Cookie is set - App.tsx will detect it and handle next steps
        console.log("✅ Token saved to cookie - waiting for App.tsx to handle navigation");
        },
      onError: () => {
        // Redirect to login after error
          setTimeout(() => {
          navigate("/auth/login", { replace: true });
          }, 3000);
        },
      });
  }, [location.search, navigate, exchangeSecret]); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine current state from React Query
  const isLoading =
    exchangeSecret.isPending ||
    (!exchangeSecret.isSuccess && !exchangeSecret.isError);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/50 to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="rounded-lg border-0 shadow-xl bg-white/95 backdrop-blur-sm p-10">
          <div className="flex flex-col items-center gap-6">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/20 to-teal-500/20 rounded-2xl blur"></div>
              <div className="relative p-4 bg-white rounded-2xl shadow-lg">
                <img
                  src="/ncoder-ai-logo.png"
                  alt="ncoder.ai"
                  className="h-20 object-contain"
                />
              </div>
            </div>
            
            {/* Loading State */}
            {isLoading && (
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
                <div className="text-center">
                  <h2 className="text-xl font-semibold text-slate-800 mb-2">
                    Processing Authentication
                  </h2>
                  <p className="text-sm text-slate-600">
                    Exchanging credentials with backend...
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesforceCallback;
